import { Home, Settings } from '@lifeomic/chromicons';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { defineMessages, useIntl } from 'react-intl';
import { evaluatedPolicySelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/evaluatedPolicy';
import { featuresSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/features';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { PageLayout } from '@lifeomic/chroma-react/components/PageLayout';
import { SettingsSlideOver } from './SettingsSlideOver';
import { useABAC } from '@lifeomic/phc-web-toolkit/src/hooks/useABAC';
import { useActiveAccount } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveAccount';
import { useActiveProject } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProject';
import { useHistory } from 'react-router-dom';
import { useQueryParams } from '@lifeomic/phc-web-toolkit/src/hooks/useQueryParams';
import { useSelector } from 'react-redux';
import {
  SmallTile,
  SmallTileContent,
} from '@lifeomic/chroma-react/components/SmallTile';
import {
  Tab,
  Tabs,
  TabList,
  TabPanel,
} from '@lifeomic/chroma-react/components/Tabs';
import * as React from 'react';
import DetailsHeader from '@lifeomic/phc-web-toolkit/src/components/DetailsHeader';
import tileConfig from '../../../../common/tiles_fl';
import AuditLog from '../Settings/AuditLog';
import clsx from 'clsx';
import { ProjectData } from '../ProjectData';

const messages = defineMessages({
  settings: {
    id: 'projects.landingPage.settings',
    defaultMessage: 'Settings',
  },
  auditTrail: {
    id: 'projects.landingPage.auditTrail',
    defaultMessage: 'Audit Trail',
  },
  tiles: {
    id: 'projects.landingPage.tiles',
    defaultMessage: 'Tiles',
  },
  data: {
    id: 'projects.landingPage.data',
    defaultMessage: 'Data',
  },
  projectLabelTabs: {
    id: 'projects.landingPage.projectLabelTabs',
    defaultMessage: 'Project level tabs',
  },
});

const useStyles = makeStyles(theme => ({
  root: {},
  backgroundCover: {
    marginBottom: theme.spacing(1),
  },
  tileContainer: {
    display: 'grid',
    gridGap: theme.spacing(4),
    gridTemplateColumns: 'repeat(6, 1fr)',
    margin: theme.spacing(3.5, 0, 4),
    transition: '0.25s ease-in',
    width: '100%',
    [theme.breakpoints.down('lg')]: {
      gridGap: theme.spacing(2),
      gridTemplateColumns: 'repeat(4, 1fr)',
      marginTop: theme.spacing(2),
      transition: '0.25s ease-in',
    },
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
      transition: '0.25s ease-in',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(1, 1fr)',
      transition: '0.25s ease-in',
    },
  },
  smallTile: {
    transition: '0.25s ease-in',
    width: 'auto',
    [theme.breakpoints.down('lg')]: {
      transition: '0.25s ease-in',
    },
  },
  smallTileContent: {
    flexDirection: 'column-reverse',
  },
  smallTileIcon: {
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  mb: {
    marginBottom: theme.spacing(4),
  },
  fullWidth: {
    width: '100%',
  },

  plotContainer: {
    display: 'grid',
    gridGap: theme.spacing(4),
    gridTemplateColumns: 'repeat(3, 1fr)',
    margin: theme.spacing(2, 0, 4),
    transition: '0.25s ease-in',
    width: '100%',
    [theme.breakpoints.down('lg')]: {
      gridGap: theme.spacing(2),
      gridTemplateColumns: 'repeat(4, 1fr)',
      transition: '0.25s ease-in',
    },
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
      transition: '0.25s ease-in',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(1, 1fr)',
      transition: '0.25s ease-in',
    },
  },
}));

type TabOptions = 'tiles' | 'data' | 'auditTrail';

export const ProjectLandingPageFountainLife: React.FC = () => {
  const classes = useStyles({});

  const { formatMessage } = useIntl();
  const { push } = useHistory();

  const [activeProject] = useActiveProject();
  const [canPerform, permissions] = useABAC();
  const activeAccount = useActiveAccount();
  const features = useSelector(featuresSelectors.selectFeatures);
  const policy = useSelector(evaluatedPolicySelectors.selectPolicyForUser);

  const [queryParams, onQueryParamPush] = useQueryParams();

  const [isSlideOverOpen, setIsSlideOverOpen] = React.useState<boolean>(false);
  const [selectedTab, setSelectedTab] = React.useState<TabOptions>(
    () => (queryParams?.tab as TabOptions) || 'tiles',
  );

  const canViewAuditTrail =
    canPerform(permissions.readData) || canPerform(permissions.readMaskedData);

  return (
    <>
      <SettingsSlideOver
        isOpen={isSlideOverOpen}
        onClose={() => setIsSlideOverOpen(!isSlideOverOpen)}
      />
      <PageLayout
        classes={{
          backgroundCover: classes.backgroundCover,
        }}
        left={
          <DetailsHeader
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            title={activeProject?.name}
            description={activeProject?.description}
            icon={Home}
          />
        }
        headerActions={
          canPerform(permissions.projectAdmin) && (
            <Button
              onClick={() => setIsSlideOverOpen(!isSlideOverOpen)}
              icon={Settings}
              variant="contained"
              color="inverse"
            >
              {formatMessage(messages.settings)}
            </Button>
          )
        }
        applyBackgroundCover
      >
        <Tabs selectedTabId={selectedTab} manualControl>
          <TabList aria-label={formatMessage(messages.projectLabelTabs)}>
            <Tab
              stopId="tiles"
              onClick={() => {
                onQueryParamPush({ tab: 'tiles' });
                setSelectedTab('tiles');
              }}
            >
              {formatMessage(messages.tiles)}
            </Tab>
            <Tab
              stopId="data"
              onClick={() => {
                onQueryParamPush({ tab: 'data' });
                setSelectedTab('data');
              }}
            >
              {formatMessage(messages.data)}
            </Tab>
            {canViewAuditTrail && (
              <Tab
                stopId="auditTrail"
                onClick={() => {
                  onQueryParamPush({ tab: 'auditTrail' });
                  setSelectedTab('auditTrail');
                }}
              >
                {formatMessage(messages.auditTrail)}
              </Tab>
            )}
          </TabList>
          <TabPanel stopId="tiles">
            <div className={classes.tileContainer}>
              {tileConfig.manageProjects.primary
                .filter(tile =>
                  typeof tile.tile.shouldDisplay === 'function'
                    ? tile.tile.shouldDisplay(activeAccount, policy, features)
                    : true,
                )
                .map((tile, index) => {
                  const Icon = tile.tile.tileIcon;

                  return (
                    <SmallTile
                      key={index}
                      role="link"
                      className={classes.smallTile}
                      onClick={() => push(tile.tile.uri)}
                    >
                      <SmallTileContent
                        className={classes.smallTileContent}
                        text={formatMessage({
                          id: tile.tile.intlId,
                          defaultMessage: tile.tile.title,
                        })}
                        fullHeight
                      >
                        <Icon className={classes.smallTileIcon} />
                      </SmallTileContent>
                    </SmallTile>
                  );
                })}
            </div>
          </TabPanel>
          <TabPanel stopId="data">
            {selectedTab === 'data' && <ProjectData />}
          </TabPanel>
          {canViewAuditTrail && (
            <TabPanel
              stopId="auditTrail"
              className={clsx(classes.fullWidth, classes.mb)}
            >
              {/*
              This is a hack to get around Plotly determining its width.
              Other tab panels should not have to do this.
              */}
              <div className={classes.plotContainer}>
                <AuditLog />
              </div>
            </TabPanel>
          )}
        </Tabs>
      </PageLayout>
    </>
  );
};

ProjectLandingPageFountainLife.displayName = 'ProjectLandingPageFountainLife';
export default ProjectLandingPageFountainLife;
